import { Container } from '@mui/material';
import { deepEqual } from 'fast-equals';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { Page } from '../../components';
import { ConfirmationDialogue } from '../../components/modal/ConfirmationDialogue';
import { AdminTable } from '../../components/table/AdminTable';
import { NumberOnlyCell } from '../../components/table/NumberOnlyCell';
import { TextCell } from '../../components/table/TextOnlyCell';
import { IMarginCategoryAPI } from '../../fetch/margin-category';
import { useToastContext } from '../../hooks';
import { IMarginCategory, MarginCategoryImplementation } from '../../models/margin-category';

export const MarginCategory: FC = () => {
  // Non-state
  const { showToast } = useToastContext();

  // Local state
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);

  // If not false, this is the row the user seeks to delete
  const [isDeletingMarginCategory, setIsDeletingMarginCategory] = useState<false | IMarginCategory>(
    false
  );

  // Local state from the network
  const [rows, setRows] = useState<IMarginCategory[]>([]);
  const [rowsCopy, setRowsCopy] = useState<IMarginCategory[]>([]);
  const [hasAnyError, setHasAnyError] = useState(false);

  const removeRow = (row: IMarginCategory): void => {
    setRows((state: IMarginCategory[]) => {
      const index = state.indexOf(row);
      const newState = state.slice();
      // Cut out the deleted row
      newState.splice(index, 1);
      return newState;
    });
  };

  const validateMarginCategories = (rows: IMarginCategory[]) => {
    const filteredRows = rows.filter((row: IMarginCategory) => {
      if (!(row.commissionAccountId || row.commissionAccountId === 0)) {
        return true;
      }
      if (!row.commissionAccountName) {
        return true;
      }
      if (!(row.generalDealCostsAccountId || row.generalDealCostsAccountId === 0)) {
        return true;
      }
      if (!row.generalDealCostsAccountName) {
        return true;
      }
      if (!row.marginCategoryName) {
        return true;
      }
      if (!(row.revenueItemId || row.revenueItemId === 0)) {
        return true;
      }
      if (!(row.revenueAccountId || row.revenueAccountId === 0)) {
        return true;
      }
      if (!row.revenueAccountName) {
        return true;
      }
      return false;
    });
    setHasAnyError(filteredRows.length > 0);
  };

  useEffect(() => {
    validateMarginCategories(rows);
  }, [rows]);

  const columns = useMemo(() => {
    return [
      {
        accesor: 'name',
        Header: 'Margin Category Name',
        verticalAlignment: 'top',
        sort: false,
        Cell: ({
          cell: {
            row: { original, index },
          },
        }: {
          cell: { row: { original: IMarginCategory; index: number } };
        }) => {
          const [marginCategoryName, setMarginCategoryName] = useState(original.marginCategoryName);

          return (
            <TextCell
              index={index}
              value={marginCategoryName}
              setValue={setMarginCategoryName}
              hasChanges={hasChanges}
              setHasChanges={setHasChanges}
              setRows={setRows}
              original={original}
              propertyName={'marginCategoryName'}
              placeholder={'Margin Category Name'}
            />
          );
        },
      },
      {
        accesor: 'revenueAccountId',
        Header: 'Revenue Account Id',
        sort: false,
        Cell: ({
          cell: {
            row: { original, index },
          },
        }: {
          cell: { row: { original: IMarginCategory; index: number } };
        }) => {
          const [revenueAccountId, setRevenueAccountId] = useState<number | string>(
            original.revenueAccountId ?? ''
          );

          return (
            <NumberOnlyCell
              index={index}
              value={revenueAccountId}
              setValue={setRevenueAccountId}
              hasChanges={hasChanges}
              setHasChanges={setHasChanges}
              setRows={setRows}
              original={original}
              propertyName={'revenueAccountId'}
              placeholder={'Revenue Account Id'}
            />
          );
        },
      },
      {
        accesor: 'name',
        Header: 'Revenue Account Name',
        verticalAlignment: 'top',
        sort: false,
        Cell: ({
          cell: {
            row: { original, index },
          },
        }: {
          cell: { row: { original: IMarginCategory; index: number } };
        }) => {
          const [revenueAccountName, setRevenueAccountName] = useState(
            original.revenueAccountName ?? ''
          );

          return (
            <TextCell
              index={index}
              value={revenueAccountName}
              setValue={setRevenueAccountName}
              hasChanges={hasChanges}
              setHasChanges={setHasChanges}
              setRows={setRows}
              original={original}
              propertyName={'revenueAccountName'}
              placeholder={'Revenue Account Name'}
            />
          );
        },
      },
      {
        accesor: 'revenueItemId',
        Header: 'Revenue Item Id',
        sort: false,
        Cell: ({
          cell: {
            row: { original, index },
          },
        }: {
          cell: { row: { original: IMarginCategory; index: number } };
        }) => {
          const [revenueItemId, setRevenueItemId] = useState<number | string>(
            original.revenueItemId ?? ''
          );

          return (
            <NumberOnlyCell
              index={index}
              value={revenueItemId}
              setValue={setRevenueItemId}
              hasChanges={hasChanges}
              setHasChanges={setHasChanges}
              setRows={setRows}
              original={original}
              propertyName={'revenueItemId'}
              placeholder={'Revenue Item Id'}
            />
          );
        },
      },

      {
        accesor: 'CommissionAccountId',
        Header: 'Commission Account Id',
        sort: false,
        Cell: ({
          cell: {
            row: { original, index },
          },
        }: {
          cell: { row: { original: IMarginCategory; index: number } };
        }) => {
          const [commmissionAccountId, setCommissionAccountId] = useState<number | string>(
            original.commissionAccountId ?? ''
          );

          return (
            <NumberOnlyCell
              index={index}
              value={commmissionAccountId}
              setValue={setCommissionAccountId}
              hasChanges={hasChanges}
              setHasChanges={setHasChanges}
              setRows={setRows}
              original={original}
              propertyName={'commissionAccountId'}
              placeholder={'Commission Account Id'}
            />
          );
        },
      },
      {
        accesor: 'CommissionAccountName',
        Header: 'Commission Account Name',
        verticalAlignment: 'top',
        sort: false,
        Cell: ({
          cell: {
            row: { original, index },
          },
        }: {
          cell: { row: { original: IMarginCategory; index: number } };
        }) => {
          const [commissionAccountName, setCommissionAccountName] = useState(
            original.commissionAccountName
          );

          return (
            <TextCell
              index={index}
              value={commissionAccountName}
              setValue={setCommissionAccountName}
              hasChanges={hasChanges}
              setHasChanges={setHasChanges}
              setRows={setRows}
              original={original}
              propertyName={'commissionAccountName'}
              placeholder={'Commission Account Name'}
            />
          );
        },
      },

      {
        accesor: 'generalDealCostsAccountName',
        Header: 'General Deal Costs Account Id',
        sort: false,
        Cell: ({
          cell: {
            row: { original, index },
          },
        }: {
          cell: { row: { original: IMarginCategory; index: number } };
        }) => {
          const [generalDealCostsAccountId, setGeneralDealCostsAccountId] = useState<
            number | string
          >(original.generalDealCostsAccountId ?? '');

          return (
            <NumberOnlyCell
              index={index}
              value={generalDealCostsAccountId}
              setValue={setGeneralDealCostsAccountId}
              hasChanges={hasChanges}
              setHasChanges={setHasChanges}
              setRows={setRows}
              original={original}
              propertyName={'generalDealCostsAccountId'}
              placeholder={'General Deal Costs Account Id'}
            />
          );
        },
      },
      {
        accesor: 'generalDealCostsAccountName',
        Header: 'General Deal Costs Account Name',
        verticalAlignment: 'top',
        sort: false,
        Cell: ({
          cell: {
            row: { original, index },
          },
        }: {
          cell: { row: { original: IMarginCategory; index: number } };
        }) => {
          const [generalDealCostsAccountName, setGeneralDealCostsAccountName] = useState(
            original.generalDealCostsAccountName
          );

          return (
            <TextCell
              index={index}
              value={generalDealCostsAccountName}
              setValue={setGeneralDealCostsAccountName}
              hasChanges={hasChanges}
              setHasChanges={setHasChanges}
              setRows={setRows}
              original={original}
              propertyName={'generalDealCostsAccountName'}
              placeholder={'General Deal Costs Account Name'}
            />
          );
        },
      },
    ];
    // Ignore showToast
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasChanges, isSaving]);

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const fetchRows = useCallback(async () => {
    setIsLoading(true);
    try {
      const categories: IMarginCategory[] = await IMarginCategoryAPI.getAll();
      setRows(categories);
      setRowsCopy(JSON.parse(JSON.stringify(categories)));
    } catch (error) {
      const message =
        error instanceof Error ? error.message : displayFailure('get', 'margin category');
      showToast('error', message);
    } finally {
      setIsLoading(false);
    }
  }, [showToast]);

  return (
    <Page title="Margin Category">
      <Container maxWidth={false} disableGutters>
        <AdminTable
          data={rows}
          columns={columns}
          isLoading={isLoading}
          hasChanges={!deepEqual(rows, rowsCopy)}
          isSaving={isSaving}
          isSaveDisabled={hasAnyError}
          onAddFocusClass="margin-category-input"
          onAdd={() => {
            // Add new empty row
            setRows(rows.concat(MarginCategoryImplementation.getDefault()));
            setRowsCopy(rows.concat(MarginCategoryImplementation.getDefault()));
            validateMarginCategories(rows);
          }}
          onSave={async function upsertMarginCategories() {
            setIsSaving(true);
            try {
              const newRows: IMarginCategory[] = await IMarginCategoryAPI.upsert(rows);

              // After successful update, update UI
              setRows(newRows);
              setRowsCopy(newRows);
              // Reset save button to disabled
              setHasChanges(false);

              showToast('success', 'Successfully saved margin category');
            } catch (error) {
              const message =
                error instanceof Error
                  ? error.message
                  : displayFailure('update', 'margin category');
              showToast('error', message);
            } finally {
              setIsSaving(false);
            }
          }}
          onDelete={(original, _index) => {
            if (
              original.marginCategoryTypeId ===
              MarginCategoryImplementation.getDefault().marginCategoryTypeId
            ) {
              showToast('info', 'Removed unsaved margin category');
              removeRow(original);
            } else {
              setIsDeletingMarginCategory(original);
            }
          }}
        />
      </Container>

      {/** ConfirmationDialogue */}
      {!!isDeletingMarginCategory && (
        <ConfirmationDialogue
          open
          id="delete-margin-category"
          title={`Delete Margin Category ${isDeletingMarginCategory.marginCategoryTypeId}`}
          onClose={() => setIsDeletingMarginCategory(false)}
          onConfirm={async function deleteMarginCategory() {
            // Check if modal is open and has proper data
            if (!isDeletingMarginCategory) {
              return;
            }
            // If this is a new, not-yet-saved entry, it does not exist in the DB
            if (
              isDeletingMarginCategory.marginCategoryTypeId ===
              MarginCategoryImplementation.getDefault().marginCategoryTypeId
            ) {
              // So, deletion only consists of removal from local state
              removeRow(isDeletingMarginCategory);
            } else {
              // Otherwise this entry already exists, hit the API to delete it
              try {
                await IMarginCategoryAPI.delete(isDeletingMarginCategory.marginCategoryTypeId);
                // Update state after only successful delete
                removeRow(isDeletingMarginCategory);
                fetchRows();
                showToast('success', 'Successfully deleted margin category.');
              } catch (error) {
                const message =
                  error instanceof Error
                    ? error.message
                    : displayFailure('delete', 'margin category');
                showToast('error', message);
              }
            }

            // Close modal
            setIsDeletingMarginCategory(false);
          }}
        />
      )}
    </Page>
  );
};

function displayFailure(operation: 'get' | 'update' | 'delete', resource: string): string {
  // Verify message ends with a period.
  const resource_ = resource.endsWith('.') ? resource.slice(0, resource.length - 1) : resource;

  return `Failed to ${operation} ${resource_}.`;
}
